import { useState, useCallback, useMemo } from "react";
import { useAssistant } from "../../config/assistantProfile.ts";
import styles from "./issaInsights.module.scss";
import type { LogResponse } from "./issaInsightsApi.ts";
import {
  preprocessAiResponse,
  renderMarkdown,
  downloadCsv,
  copyResponseToClipboard,
  formatDate,
  formatElapsedSeconds,
} from "./issaResponseHelpers.ts";
import { IssaActionButtons } from "./issaChatActions.tsx";
import { ImageLightbox } from "./imageLightbox.tsx";
import { IssaAnswerFile } from "./issaAnswerFiles.tsx";
import { IssaChatAvatar, avatarModelClass } from "./issaChatAvatar.tsx";
import { IssaAnswerFeedback } from "./issaAnswerFeedback.tsx";
import { isFeedbackRating, isSavedAnswerId, type FeedbackRating } from "./issaFeedback.ts";

/** The rating line under an answer. Given only where feedback can be saved. */
export interface AnswerFeedbackProps {
  /** Ask "How was this answer?" under this answer now. */
  ask: boolean;
  onRated: (logId: number, rating: FeedbackRating) => void;
  onDismiss: () => void;
}

export function MessagePair({ log, modelConfigId, showAvatar = true, onUseNext, feedback }: {
  log: LogResponse;
  modelConfigId?: string;
  showAvatar?: boolean;
  /** Sends the answer's suggested follow-up. Given only for the latest answer. */
  onUseNext?: (text: string) => void;
  feedback?: AnswerFeedbackProps;
}) {
  const [copied, setCopied] = useState(false);
  const assistant = useAssistant();
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  // How long the answer took: submission ([When]) to completion ([Updated]).
  const responseDurationSeconds = log.Updated
    ? Math.max(0, Math.round((new Date(log.Updated).getTime() - new Date(log.When).getTime()) / 1000))
    : null;

  // Pre-process once
  const { displayText, csvBlocks, actionButtons, images, fileIds, nextPrompt } = log.Response
    ? preprocessAiResponse(log.Response, { openableImages: true })
    : { displayText: "", csvBlocks: [] as string[], actionButtons: [], images: [] as string[], fileIds: [] as string[], nextPrompt: null };
  const responseHtml = displayText ? renderMarkdown(displayText) : "";
  const filesById = useMemo(
    () => new Map((log.Files ?? []).map(file => [file.ExportId.toLowerCase(), file])),
    [log.Files],
  );

  // Split rendered HTML on CSV/action/file placeholders to interleave real React controls
  const htmlSegments = responseHtml ? responseHtml.split(/<!--(CSV_EXPORT_\d+|ISSA_ACTION_\d+|ISSA_FILE_\d+)-->/) : [];

  // The answer HTML is injected (markdown), so image clicks are caught by
  // delegation. Each generated picture sits in a `data-image-open` button, so
  // Enter/Space on it arrives here as a click on the button, not on the image.
  const handleAnswerClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const opener = target?.closest("[data-image-open]");
    const image = opener?.querySelector("img") ?? (target instanceof HTMLImageElement ? target : null);
    if (image?.src) setLightboxSrc(image.src);
  }, []);

  const handleCopy = useCallback(async () => {
    await copyResponseToClipboard(log.Response, log.Files);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [log.Response, log.Files]);

  return (
    <>
      {/* User message */}
      <div className={styles.messageUser}>
        <div className={styles.messageBody}>
          <div className={styles.bubbleUser}>{log.Prompt}</div>
          <div className={styles.messageTime}>{formatDate(log.When)}</div>
        </div>
      </div>
      {/* AI response */}
      {log.Response && (
        <div className={styles.messageAi}>
          {showAvatar && <IssaChatAvatar show modelClass={avatarModelClass(modelConfigId, assistant.modelConfigId)} />}
          <div className={styles.messageBody}>
            <div className={styles.bubbleAi} onClick={handleAnswerClick}>
              {htmlSegments.map((seg, i) => {
                // Odd indices are the captured placeholder tokens
                if (i % 2 === 1) {
                  if (seg.startsWith("CSV_EXPORT_")) {
                    const csvIdx = parseInt(seg.slice("CSV_EXPORT_".length), 10);
                    const csv = csvBlocks[csvIdx];
                    if (!csv) return null;
                    return (
                      <div key={`csv-${csvIdx}`} className={styles.responseActions}>
                        <button type="button" className={styles.exportBtn}
                          onClick={() => downloadCsv(csv, `issa-export-${csvIdx + 1}.csv`)}>
                          Export table
                        </button>
                      </div>
                    );
                  }
                  if (seg.startsWith("ISSA_FILE_")) {
                    const fileIdx = parseInt(seg.slice("ISSA_FILE_".length), 10);
                    const exportId = fileIds[fileIdx];
                    return exportId ? (
                      <IssaAnswerFile key={`file-${fileIdx}`} exportId={exportId} file={filesById.get(exportId)} />
                    ) : null;
                  }
                  const actionIdx = parseInt(seg.slice("ISSA_ACTION_".length), 10);
                  const action = actionButtons[actionIdx];
                  return action ? (
                    <IssaActionButtons
                      key={`action-${actionIdx}`}
                      action={action}
                      messageId={log.Id}
                      index={actionIdx}
                      images={images}
                      fromAttachment={log.UsedAttachments === true}
                    />
                  ) : null;
                }
                // Even indices are HTML content
                return seg ? <span key={i} dangerouslySetInnerHTML={{ __html: seg }} /> : null;
              })}
            </div>
            {/* The suggested next step, as one chip: the whole suggestion is the
                target, it reads straight on from the answer, and it comes before
                the utility actions below. The Tab badge points at the keyboard
                route in the message box. */}
            {nextPrompt && onUseNext && (
              <button
                type="button"
                className={styles.nextChip}
                aria-label={`Ask: ${nextPrompt}`}
                onClick={() => onUseNext(nextPrompt)}
              >
                <svg className={styles.nextChipIcon} viewBox="0 0 24 24" width="14" height="14" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M15 10l5 5-5 5" />
                  <path d="M4 4v7a4 4 0 0 0 4 4h12" />
                </svg>
                <span className={styles.nextChipText}>{nextPrompt}</span>
                <kbd className={styles.nextChipKey} aria-hidden="true">Tab</kbd>
              </button>
            )}
            <div className={styles.responseActions}>
              <button type="button" className={styles.exportBtn} onClick={handleCopy}>
                <span aria-hidden="true">{copied ? "✓" : "📋"}</span> {copied ? "Copied" : "Copy response"}
              </button>
              <span className={styles.srOnly} role="status">{copied ? "Response copied to the clipboard" : ""}</span>
              {feedback && isSavedAnswerId(log.Id) && (
                <IssaAnswerFeedback
                  logId={log.Id}
                  rating={isFeedbackRating(log.FeedbackRating) ? log.FeedbackRating : null}
                  ask={feedback.ask}
                  onRated={feedback.onRated}
                  onDismiss={feedback.onDismiss}
                />
              )}
            </div>
            {lightboxSrc && (
              <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
            )}
            <div
              className={styles.messageTime}
              title={
                responseDurationSeconds !== null
                  ? `Took ${formatElapsedSeconds(responseDurationSeconds)}`
                  : undefined
              }
            >
              {formatDate(log.Updated ?? log.When)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
